import { RowCrudStatus } from "../../common/types";
import { AssociatedDocumentEdition } from "../../../api/graphql";
import { LangStore } from "../../../store";
import { FileCRUDType, fileCRUDInit } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { editionDocumentTypeDisplayId } from "../../editionDocumentType/store/EditionDocumentTypeCommonStore";

let uniqueCounter = 1;
export type AssociatedDocumentEditionValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  name: string;
  file: string;
  file_data: FileCRUDType;
  __file_files: FileCRUDType[];
  __type: string;
  showType: { displayId: string; id: string } | null;
  notes: string;
  publisherConfigurationDisplay: string;
  authorsDisplay: string;
};

export type AssociatedDocumentEditionErrorsType = {
  name?: { message: string; fieldLabel: string };
  file?: { message: string; fieldLabel: string };
  type?: { message: string; fieldLabel: string };
  notes?: { message: string; fieldLabel: string };
  publisherConfigurationDisplay?: { message: string; fieldLabel: string };
  authorsDisplay?: { message: string; fieldLabel: string };
};

export function AssociatedDocumentEditionValuesInit(): AssociatedDocumentEditionValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    name: "",
    file: "",
    file_data: fileCRUDInit(),
    __file_files: [],
    __type: "",
    showType: null,
    notes: "",
    publisherConfigurationDisplay: "",
    authorsDisplay: "",
  };
}

export type AssociatedDocumentEditionType =
  | AssociatedDocumentEdition
  | AssociatedDocumentEditionValuesType;

export function associatedDocumentEditionDisplayId(
  source: Partial<AssociatedDocumentEdition>,
  _langStore: LangStore
) {
  return source.name ?? _langStore.emptyStringSymbol;
}

export function getAssociatedDocumentEditionValues(
  source: AssociatedDocumentEditionType,
  _langStore: LangStore,
  _duplicated: boolean = false
): AssociatedDocumentEditionValuesType {
  const entityValues = AssociatedDocumentEditionValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : associatedDocumentEditionDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.name = source.name;
  entityValues.__file_files =
    source.file_data && !_duplicated
      ? [
          {
            id:
              "id" in source.file_data && source.file_data.id
                ? source.file_data.id
                : `generated_${uniqueCounter++}`,
            name: source.file_data.name,
            description: source.file_data.description || undefined,
            size: source.file_data.size || 0,
            formattedSize: source.file_data.size
              ? formatBytes(source.file_data.size)
              : "",
            mimetype: source.file_data.mimetype,
            url: source.file_data.url || undefined,
            __status__: "original",
          },
        ]
      : [];
  entityValues.file = source.file && !_duplicated ? source.file : "";
  entityValues.file_data =
    source.file_data && "__status__" in source.file_data
      ? source.file_data
      : source.file_data
      ? {
          id: ("id" in source.file_data && source.file_data.id
            ? source.file_data.id
            : `generated_${uniqueCounter++}`) as string,
          name: source.file_data.name,
          description: source.file_data.description || undefined,
          size: source.file_data.size || 0,
          formattedSize: source.file_data.size
            ? formatBytes(source.file_data.size)
            : "",
          mimetype: source.file_data.mimetype,
          url: source.file_data.url || undefined,
          __status__: "original" as RowCrudStatus,
        }
      : fileCRUDInit();
  entityValues.showType =
    "type" in source && source.type
      ? {
          id: source.type.id,
          displayId: editionDocumentTypeDisplayId(source.type, _langStore),
        }
      : "showType" in source && source.showType
      ? { id: source.showType.id, displayId: source.showType.displayId }
      : null;
  entityValues.__type = entityValues.showType?.displayId || "";
  entityValues.notes = source.notes || "";
  entityValues.publisherConfigurationDisplay =
    source.publisherConfigurationDisplay || "";
  entityValues.authorsDisplay = source.authorsDisplay || "";
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}
